import React from 'react';
import type { Room, Bed } from '../lib/api';
import { fmtDate } from '../lib/format';

function bedIcon(type: string): string {
  if (type === 'QUEEN' || type === 'KING') return 'Q';
  if (type === 'TWIN') return 'T';
  return 'S';
}

type BedState = 'free' | 'occupied' | 'future' | 'blocked';

function bedState(bed: Bed): BedState {
  if (bed.isBlocked) return 'blocked';
  if (bed.currentStay) return 'occupied';
  return 'free';
}

interface BedRowProps {
  bed: Bed;
  onClick: (bed: Bed) => void;
}

function BedRow({ bed, onClick }: BedRowProps) {
  const state = bedState(bed);

  const statusBadge = (() => {
    switch (state) {
      case 'occupied': return <span className="bed-status occupied">● Occupied</span>;
      case 'blocked':  return <span className="bed-status blocked">● Blocked</span>;
      default:         return <span className="bed-status available">● Free</span>;
    }
  })();

  return (
    <div
      className={`bed ${state}`}
      onClick={() => onClick(bed)}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onClick(bed); }}
    >
      <div style={{ flex: 1 }}>
        <div className="bed-info">
          <span className="bed-icon">{bedIcon(bed.bedType)}</span>
          <span className="bed-label">{bed.label}</span>
          <span className="bed-type">{bed.bedType}</span>
        </div>
        {bed.activeBlock && (
          <div className="bed-block-detail">
            🔒 {bed.activeBlock.reason}
          </div>
        )}
        {bed.currentStay && (
          <div className="bed-occ-detail">
            👤 <strong>{bed.currentStay.occupantName}</strong>
            <span style={{ marginLeft: 6, color: 'var(--text-secondary)' }}>
              until {fmtDate(bed.currentStay.departureDate)}
            </span>
          </div>
        )}
      </div>
      {statusBadge}
    </div>
  );
}

interface RoomCardProps {
  room: Room;
  onBedClick: (room: Room, bed: Bed) => void;
  onHousekeepingClick?: (room: Room) => void;
}

function RoomCard({ room, onBedClick, onHousekeepingClick }: RoomCardProps) {
  return (
    <div className="room">
      <div className="room-header">
        <div>
          <span className="room-num">Room {room.number}</span>
          {room.displayName && <span className="room-name">{room.displayName}</span>}
        </div>
        <span
          className={`room-hsk ${room.housekeepingStatus} ${onHousekeepingClick ? 'clickable' : ''}`}
          onClick={onHousekeepingClick ? (e) => { e.stopPropagation(); onHousekeepingClick(room); } : undefined}
          role={onHousekeepingClick ? 'button' : undefined}
          tabIndex={onHousekeepingClick ? 0 : undefined}
          onKeyDown={onHousekeepingClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onHousekeepingClick(room); } : undefined}
          title={onHousekeepingClick ? 'Click to change housekeeping status' : undefined}
        >
          {room.housekeepingStatus === 'CLEAN' ? '✓ Clean' : '⚠ Dirty'}
        </span>
      </div>
      <div className="bed-list">
        {room.beds.map(bed => (
          <BedRow key={bed.id} bed={bed} onClick={() => onBedClick(room, bed)} />
        ))}
      </div>
    </div>
  );
}

interface RoomRackProps {
  rooms: Room[];
  onBedClick: (room: Room, bed: Bed) => void;
  onHousekeepingClick?: (room: Room) => void;
}

export function RoomRack({ rooms, onBedClick, onHousekeepingClick }: RoomRackProps) {
  if (rooms.length === 0) {
    return <div className="loading">No rooms configured.</div>;
  }
  return (
    <div className="room-grid">
      {rooms.map(r => <RoomCard key={r.id} room={r} onBedClick={onBedClick} onHousekeepingClick={onHousekeepingClick} />)}
    </div>
  );
}

export function RoomRackLegend() {
  return (
    <div className="legend">
      <strong>Legend:</strong>
      <span className="legend-item">
        <span className="legend-swatch" style={{ background: '#C0DD97', borderColor: '#639922' }} />
        Available (click to book)
      </span>
      <span className="legend-item">
        <span className="legend-swatch" style={{ background: '#B5D4F4', borderColor: '#185FA5' }} />
        Occupied
      </span>
      <span className="legend-item">
        <span className="legend-swatch" style={{ background: '#F09595', borderColor: '#A32D2D' }} />
        Blocked
      </span>
      <span className="legend-item">
        <span className="legend-swatch" style={{ background: '#FAC775', borderColor: '#BA7517' }} />
        Dirty
      </span>
    </div>
  );
}

export function RoomRackStats({ rooms }: { rooms: Room[] }) {
  let totalBeds = 0, free = 0, occupied = 0, blocked = 0, dirty = 0;
  for (const r of rooms) {
    if (r.housekeepingStatus === 'DIRTY') dirty++;
    for (const b of r.beds) {
      totalBeds++;
      const s = bedState(b);
      if (s === 'blocked') blocked++;
      else if (s === 'occupied') occupied++;
      else free++;
    }
  }
  return (
    <div className="stats">
      <strong>{rooms.length}</strong> rooms · <strong>{totalBeds}</strong> beds ·{' '}
      <strong>{free}</strong> free · <strong>{occupied}</strong> occupied ·{' '}
      <strong>{blocked}</strong> blocked · <strong>{dirty}</strong> dirty
    </div>
  );
}
